import { Component, OnInit, AfterViewInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Chart, registerables } from 'chart.js';
import { FilterService } from '../../services/filter.service';
import { SharedDataService } from '../../services/shared-data.service';
import { AudienceDashboardAdapterService } from '../../services/audience-engine-adapter.service';
import { AudienceDashboardService } from '../../services/audience-engine.service';
import { Subscription, forkJoin, of } from 'rxjs';
import { filter, take, skip } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';

Chart.register(...registerables);

type DateRangeOption = '7days' | '14days' | '30days' | '90days' | 'year';
type DeviceMetricId = 'device' | 'source' | 'goal';

interface DeviceMetricSlice {
  label: string;
  value: number;
  raw: number;
  color: string;
  tooltip: string;
}

@Component({
  selector: 'app-personas',
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './personas.html',
  standalone: true,
  styleUrl: './personas.scss'
})
export class PersonasComponent implements OnInit, AfterViewInit, OnDestroy {
  isTableView = false;
  expandedRows: any[] = [];
  private filterSubscription: Subscription = new Subscription();
  isLoading = false;
  private isInitialLoad = true;
  private filtersLoaded = false;

  // Chart.js instances — kept so they can be destroyed in ngOnDestroy,
  // otherwise they leak (orphaned in Chart.js registry) on every navigation.
  private totalUsersChart?: Chart;
  private usersCategoryChart?: Chart;
  private usersDeviceChart?: Chart;
  private audienceChart?: Chart;


  // Device chart metric selector (same as dashboard)
  selectedDeviceMetric: DeviceMetricId = 'device'; // default to device percentage
  availableDeviceMetrics: Array<{ id: DeviceMetricId; name: string }> = [
    { id: 'device', name: 'dashboard.deviceMetrics.device' },
    { id: 'source', name: 'dashboard.deviceMetrics.source' },
    { id: 'goal', name: 'dashboard.deviceMetrics.goal' }
  ];
  private readonly metricPalette = [
    '#1e3a8a', '#3b82f6', '#f97316', '#fbbf24', '#10b981',
    '#8b5cf6', '#ec4899', '#14b8a6', '#ef4444', '#6366f1'
  ];

  totalUsersHasData = true;
  usersCategoryHasData = true;
  usersDeviceHasData = true;
  audienceTrendHasData = true;
  treemapHasData = true;

  // Tooltip properties for treemap
  private usersDeviceDataByMetric: Record<DeviceMetricId, DeviceMetricSlice[]> = {
    device: [],
    source: [],
    goal: []
  };
  usersDeviceLegend: Array<{ label: string; fullLabel: string; color: string }> = [];

  // Column visibility controls
  columnVisibility = {
    personas: true,
    source: true,
    device: true,
    users: true,
    goal: true
  };
  private readonly selectableColumns: Array<'source' | 'device' | 'goal'> = ['source', 'device', 'goal'];

  showColumnSelector = false;

  constructor(
    private filterService: FilterService,
    private cdr: ChangeDetectorRef,
    private sharedDataService: SharedDataService,
    private audienceDashboardAdapter: AudienceDashboardAdapterService,
    private audienceDashboardService: AudienceDashboardService
  ) {}

  toggleTableView() {
    this.isTableView = !this.isTableView;
    if (this.isTableView) {
      this.columnVisibility.personas = true;
      this.columnVisibility.users = true;
      this.updateExpandedRows();
    } else {
      // Reinitialize charts when returning to chart view
      this.isLoading = true;
      this.sharedDataService.setLoading('personas', true);
      setTimeout(() => {
        if (this.filtersLoaded) {
          this.loadPersonasChartsFromAPI();
        }
      });
    }
  }

  toggleColumnSelector() {
    this.showColumnSelector = !this.showColumnSelector;
  }

  onColumnVisibilityChange() {
    this.updateExpandedRows();
  }

  toggleColumn(column: string) {
    if (!this.selectableColumns.includes(column as 'source' | 'device' | 'goal')) {
      return;
    }
    this.columnVisibility[column as keyof typeof this.columnVisibility] = !this.columnVisibility[column as keyof typeof this.columnVisibility];
    // Recalculate table data when column visibility changes
    this.updateExpandedRows();
  }

  selectAllColumns() {
    this.selectableColumns.forEach(column => {
      this.columnVisibility[column] = true;
    });
    this.columnVisibility.personas = true;
    this.columnVisibility.users = true;
    this.updateExpandedRows();
  }

  areAllColumnsSelected(): boolean {
    return this.selectableColumns.every(column => this.columnVisibility[column]);
  }

    // Computed property for selected audiences
  get selectedAudiences() {
    return this.filterService.getSelectedAudiences();
  }

  updateExpandedRows() {

    const showTableSpinner = this.isTableView;
    if (showTableSpinner) {
      this.isLoading = true;
      this.sharedDataService.setLoading('personas', true);
    }

    if (!this.filtersLoaded) {
      this.expandedRows = [];
      return;
    }

    // Prepare visible columns for backend
    const visibleColumns = Object.keys(this.columnVisibility).filter((key)=>key!=='users' && !!(this.columnVisibility as any)[key]);

    // Build FilterColumns from selected device, source, and goal
    const selectedDevices = this.filterService.getSelectedDevices().map(d => d.id);
    const selectedSources = this.filterService.getSelectedTrafficSources().map(s => s.id);
    const selectedGoal = this.filterService.getSelectedGoal();

    const filterColumns: string[] = [
      ...selectedDevices,
      ...selectedSources,
      ...(selectedGoal ? [selectedGoal.id] : [])
    ];


    // Load table data from API with column visibility and filters
    this.audienceDashboardAdapter.getTableData(visibleColumns, filterColumns).subscribe({
      next: (allTableData) => {

        // Backend already does aggregation based on visible columns
        // We just need to filter based on sidebar selections
        const selectedAudiences = this.filterService.getSelectedAudiences().map(a => a.id);
        const selectedDevices = this.filterService.getSelectedDevices().map(d => d.id);
        const selectedSources = this.filterService.getSelectedTrafficSources().map(s => s.id);


        // Backend already filters by device/source/goal based on sidebar selections
        // We only need to filter by personas for VISUALIZATION (which personas to show in table)
        const selectedAudienceSet = new Set(selectedAudiences);

        const filteredData = !this.columnVisibility.personas || selectedAudienceSet.size === 0
          ? allTableData  // If personas column hidden or nothing selected, show all
          : allTableData.filter(row => {
              const audienceId =
                row.audience_id ??
                row.personas_id ??
                row.audienceId ??
                row.personasId;
              return audienceId ? selectedAudienceSet.has(audienceId) : false;
            });


        const aggregatedData = this.aggregateTableRows(filteredData);

        // Group by audience_id to show audience name only once
        const groupedByAudience: { [key: string]: any[] } = {};
        aggregatedData.forEach(row => {
          const audienceKey = this.columnVisibility.personas
            ? (row.audience_id ?? row.personas_id ?? row.audienceId ?? row.personasId ?? 'unknown_audience')
            : 'all';
          if (!groupedByAudience[audienceKey]) {
            groupedByAudience[audienceKey] = [];
          }
          groupedByAudience[audienceKey].push(row);
        });

        // Build expandedRows array
        this.expandedRows = [];
        const allUserCounts = aggregatedData.map(row => row.user_count);
        const maxUsers = allUserCounts.length > 0 ? Math.max(...allUserCounts) : 1;

        Object.keys(groupedByAudience).forEach(audienceId => {
          const rows = groupedByAudience[audienceId];
          rows.forEach((row, index) => {
            const personaName =
              row.audience_name ??
              row.personas_name ??
              row.audience ??
              row.personas ??
              'Unknown Audience';
            const displayName = this.columnVisibility.personas && index === 0 ? personaName : '';
            this.expandedRows.push({
              audience: displayName,
              personas: displayName,
              source: row.source_name, // Pass through actual value - HTML will hide with *ngIf if needed
              device: row.device_name, // Pass through actual value - HTML will hide with *ngIf if needed
              goal: row.goal_name, // Pass through actual value - HTML will hide with *ngIf if needed
              userCount: Number(row.user_count) || 0,
              userProgress: maxUsers > 0 ? (Number(row.user_count) / maxUsers) * 100 : 0
            });
          });
        });

        if (showTableSpinner) {
          this.isLoading = false;
          this.sharedDataService.setLoading('personas', false);
        }
      },
      error: (error) => {
        console.error('[PERSONAS] Error loading table data:', error);
        // Fallback to empty array on error
        this.expandedRows = [];
        if (showTableSpinner) {
          this.isLoading = false;
          this.sharedDataService.setLoading('personas', false);
        }
      }
    });
  }

  /**
   * Aggrega i dati della tabella in funzione delle colonne visibili.
   * Quando una colonna è nascosta, i record vengono sommati per evitare duplicati visivi.
   */
  private aggregateTableRows(rows: any[]): any[] {
    if (!rows || rows.length === 0) {
      return [];
    }

    const groupedRows = new Map<string, any>();

    rows.forEach(rawRow => {
      const normalizedRow = {
        ...rawRow,
        audience_id: rawRow.audience_id ?? rawRow.personas_id ?? rawRow.audienceId ?? rawRow.personasId ?? 'unknown_audience',
        audience_name: rawRow.audience_name ?? rawRow.personas_name ?? rawRow.audience ?? rawRow.personas ?? 'Unknown Audience',
        source_id: this.columnVisibility.source ? (rawRow.source_id ?? 'unknown_source') : '__all__',
        source_name: this.columnVisibility.source
          ? (rawRow.source_name ?? rawRow.source ?? 'Unknown Source')
          : 'All Sources',
        device_id: this.columnVisibility.device ? (rawRow.device_id ?? 'unknown_device') : '__all__',
        device_name: this.columnVisibility.device
          ? (rawRow.device_name ?? rawRow.device ?? 'Unknown Device')
          : 'All Devices',
        goal_id: this.columnVisibility.goal ? (rawRow.goal_id ?? 'unknown_goal') : '__all__',
        goal_name: this.columnVisibility.goal
          ? (rawRow.goal_name ?? rawRow.goal ?? 'Unknown Goal')
          : 'All Goals'
      };

      const key = [
        normalizedRow.audience_id,
        normalizedRow.source_id,
        normalizedRow.device_id,
        normalizedRow.goal_id
      ].join('|');

      const rowCount = Number(rawRow.user_count ?? rawRow.userCount) || 0;

      if (groupedRows.has(key)) {
        const existing = groupedRows.get(key);
        existing.user_count = (Number(existing.user_count) || 0) + rowCount;
      } else {
        groupedRows.set(key, {
          ...normalizedRow,
          user_count: rowCount
        });
      }
    });

    return Array.from(groupedRows.values());
  }

  // Reinitialize charts when selection changes
  reinitializeCharts() {
    // Skip reinitialization during initial load to avoid double loading
    if (this.isInitialLoad) {
      return;
    }
    if (!this.filtersLoaded) {
      return;
    }

    if (!this.isTableView) {
      // Reload charts from API when filters change
      this.loadPersonasChartsFromAPI();
    }
  }

  ngOnInit() {
    // Set loading state immediately and notify SharedDataService
    this.isLoading = true;
    this.sharedDataService.setLoading('personas', true);
    this.filtersLoaded = this.filterService.areFiltersLoaded();

    // Wait for filters to be loaded before switching context
    this.filterSubscription.add(
      this.filterService.filtersLoaded$.subscribe(loaded => {
        this.filtersLoaded = loaded;
        if (!loaded) {
          this.isLoading = true;
          this.sharedDataService.setLoading('personas', true);
          return;
        }
        // Switch filter context to PERSONAS audiences AFTER filters are loaded
        this.filterService.switchAudienceContext('personas');

        // NOTE: Non chiamare updateExpandedRows qui - sarà chiamato solo quando si passa in vista tabella
      })
    );

    // NOTE: Rimosso updateExpandedRows() dall'init - viene chiamato solo quando isTableView=true

    // DON'T load charts here - wait for ngAfterViewInit when canvas elements are ready

    // Subscribe to filter changes
    this.filterSubscription.add(
      this.filterService.selectedAudiences$.subscribe(() => {
        // Carica i dati tabella solo se siamo in vista tabella
        if (this.isTableView) {
          this.updateExpandedRows();
        }
        this.reinitializeCharts();
      })
    );

    // Subscribe to date range changes
    this.filterSubscription.add(
      this.filterService.selectedDateRange$.subscribe(() => {
        this.reinitializeCharts();
      })
    );

    // Subscribe to device filter changes
    this.filterSubscription.add(
      this.filterService.selectedDevices$.subscribe(() => {
        this.reinitializeCharts();
      })
    );

    // Subscribe to traffic source filter changes
    this.filterSubscription.add(
      this.filterService.selectedTrafficSources$.subscribe(() => {
        this.reinitializeCharts();
      })
    );

    // Subscribe to goal filter changes
    this.filterSubscription.add(
      this.filterService.selectedGoal$.subscribe(() => {
        this.reinitializeCharts();
      })
    );

    this.filterSubscription.add(
      this.filterService.applyFiltersToAllCharts$.pipe(
        skip(1)
      ).subscribe(() => {
        if (this.isTableView) {
          this.updateExpandedRows();
        }
        this.reinitializeCharts();
      })
    );
  }

  ngOnDestroy() {
    this.filterSubscription.unsubscribe();
    this.destroyAllCharts();
  }

  private destroyAllCharts(): void {
    this.totalUsersChart?.destroy();
    this.usersCategoryChart?.destroy();
    this.usersDeviceChart?.destroy();
    this.audienceChart?.destroy();
    this.totalUsersChart = undefined;
    this.usersCategoryChart = undefined;
    this.usersDeviceChart = undefined;
    this.audienceChart = undefined;
  }

  ngAfterViewInit() {
    // Canvas elements are now ready in the DOM
    // Wait for filters to be loaded before loading charts
    this.filterService.filtersLoaded$.pipe(
      // Wait until filters are actually loaded
      filter(loaded => loaded === true),
      // Only take the first emission
      take(1)
    ).subscribe(() => {
      this.loadPersonasChartsFromAPI();

      // Mark initial load as complete after data loads
      this.isInitialLoad = false;
    });
  }

  /**
   * Load all personas charts from API (independent of Dashboard)
   */
  private loadPersonasChartsFromAPI() {
    if (!this.filtersLoaded) {
      return;
    }
    this.isLoading = true;
    this.sharedDataService.setLoading('personas', true);

    // Get current filter selections
    const selectedAudiences = this.filterService.getSelectedAudiences();
    const dateRange = this.filterService.getSelectedDateRange();

    // Convert date range to days
    const daysBack = this.convertDateRangeToDays(dateRange);
    const comparisonDaysBack = this.getComparisonDaysBack(dateRange);
    this.audienceDashboardAdapter.setDaysBack(daysBack);

    // Get audience IDs and colors
    const audienceIds = selectedAudiences.map(aud => aud.id);
    const audienceColors: { [key: string]: string } = {};
    selectedAudiences.forEach(aud => {
      audienceColors[aud.id] = aud.color || '#3b82f6';
    });

    // Get filters and build audience columns
    const allFilters = this.filterService.getAllFilters();

    // Build FilterColumns from selected device, source, and goal (NOT personas)
    const selectedDevices = this.filterService.getSelectedDevices().map(d => d.id);
    const selectedSources = this.filterService.getSelectedTrafficSources().map(s => s.id);
    const selectedGoal = this.filterService.getSelectedGoal();

    const filterColumns: string[] = [
      ...selectedDevices,
      ...selectedSources,
      ...(selectedGoal ? [selectedGoal.id] : [])
    ];


    // Personas audience columns are used ONLY for visualization (which lines to show in charts)
    const audienceColumns = allFilters
      .filter(f => {
        const objectType = (f.ObjectType || '').toLowerCase();
        return (objectType === 'personas' || objectType === 'persona') && audienceIds.includes(f.ColumnName);
      })
      .map(f => f.ColumnName);


    if (audienceColumns.length === 0) {
      console.error('No personas audience columns found in filters.');
      console.error('Audience IDs requested:', audienceIds);
      console.error('Available ObjectTypes:', [...new Set(allFilters.map(f => f.ObjectType))]);
      this.isLoading = false;
      this.sharedDataService.setLoading('personas', false);
      alert('No personas audiences found. Please check your data configuration.');
      return;
    }

    // OPTIMIZATION: Load shared data (totals + daily) ONCE before processing
    // Similar to Public component optimization

    const filters = this.filterService.getAllFilters();

    const applyFiltersToPrimaryCharts = this.filterService.getApplyFiltersToAllCharts();
    const baseFilterColumns = applyFiltersToPrimaryCharts ? filterColumns : [];

    const totalsFiltered$ = this.audienceDashboardService.getChartTotals(daysBack, filterColumns);
    const dailyFiltered$ = this.audienceDashboardService.getChartDailyTrend(daysBack, filterColumns, audienceColumns);
    const totalsBase$ = this.audienceDashboardService.getChartTotals(daysBack, baseFilterColumns);
    const dailyBase$ = this.audienceDashboardService.getChartDailyTrend(daysBack, baseFilterColumns, audienceColumns);
    const comparisonDaily$ = comparisonDaysBack > daysBack
      ? this.audienceDashboardService.getChartDailyTrend(comparisonDaysBack, baseFilterColumns, [])
      : of(null);

    forkJoin({
      totalsFiltered: totalsFiltered$,
      dailyFiltered: dailyFiltered$,
      totalsBase: totalsBase$,
      dailyBase: dailyBase$,
      comparisonDaily: comparisonDaily$
    }).subscribe({
      next: (sharedData) => {

        const totalUsersData = this.processTotalUsersData(
          sharedData.totalsBase,
          sharedData.dailyBase,
          dateRange,
          sharedData.comparisonDaily
        );
        const usersCategoryData = this.processUsersCategoryData(sharedData.totalsBase, filters);
        const usersDeviceDataByMetric = this.processUsersDeviceData(sharedData.totalsBase, filters);

        const audiencePublicData = this.processAudiencePublicData(sharedData.dailyFiltered, audienceColumns);

        // Process treemap data if needed
        const treemapData = audienceIds.length > 0
          ? this.processTreemapData(sharedData.totalsFiltered, filters, audienceIds, audienceColors)
          : [];
        this.treemapHasData = Array.isArray(treemapData) && treemapData.length > 0;

        // Create all charts with the processed data
        const allData = {
          totalUsers: totalUsersData,
          usersCategory: usersCategoryData,
          usersDevice: usersDeviceDataByMetric,
          audiencePublic: audiencePublicData,
          audienceTreemap: treemapData
        };


        // Destroy existing charts to prevent canvas reuse issues
        this.destroyAllCharts();

        // Create all charts
        this.createTotalUsersChart(allData.totalUsers.chartData);
        this.updatePersonasValues(allData.totalUsers);
        this.createUsersCategoryPersonasChart(allData.usersCategory);
        this.usersDeviceDataByMetric = usersDeviceDataByMetric;
        this.createUsersDevicePersonasChart();
        this.createAudiencePersonasChart(allData.audiencePublic);

        // Update treemap layout (swap with cards)
        setTimeout(() => {
          this.updateTreemapData(treemapData || []);
        }, 0);

        // Store data in SharedDataService for future reuse
        this.sharedDataService.setPublicData({
          totalUsers: allData.totalUsers,
          usersCategory: allData.usersCategory,
          usersDevice: allData.usersDevice,
          audiencePublic: allData.audiencePublic,
          audienceTreemap: allData.audienceTreemap,
          filters: {
            audienceColumns,
            daysBack,
            selectedAudiences,
            audienceIds,
            audienceColors
          }
        });

        // Hide loading ONLY after ALL charts are ready
        this.isLoading = false;
        this.sharedDataService.setLoading('personas', false);
      },
      error: (error) => {
        console.error('Error loading personas shared data:', error);
        this.isLoading = false;
        this.sharedDataService.setLoading('personas', false);
        this.usersDeviceDataByMetric = { device: [], source: [], goal: [] };
        this.usersDeviceLegend = [];
        this.usersDeviceHasData = false;
        this.updateTreemapData([]);
        alert('Error loading personas data: ' + error.message);
      }
    });
  }

  /**
   * Convert date range string to number of days
   */
  private convertDateRangeToDays(range: DateRangeOption): number {
    switch (range) {
      case '7days': return 7;
      case '14days': return 14;
      case '30days': return 30;
      case '90days': return 90;
      case 'year': return 365;
      default: return 30;
    }
  }

  private getComparisonDaysBack(range: DateRangeOption): number {
    switch (range) {
      case '7days':
        return 30;
      case '14days':
        return 30;
      default:
        return this.convertDateRangeToDays(range);
    }
  }

  private getWindowSizeForRange(range: DateRangeOption): number | null {
    switch (range) {
      case '7days':
        return 7;
      case '14days':
        return 14;
      default:
        return null;
    }
  }

  private sumTncIds(entries: any[]): number {
    return entries.reduce((sum, item) => sum + (Number(item?.TNCIDs) || 0), 0);
  }

  private sortDailyEntries(entries: any[]): any[] {
    return [...(entries || [])].sort((a, b) => {
      const dateA = new Date(a?.Date || a?.date || 0).getTime();
      const dateB = new Date(b?.Date || b?.date || 0).getTime();
      return dateA - dateB;
    });
  }

  private updateTreemapData(treemapData: any[]) {
    const currentAudiences = Array.isArray(treemapData) ? treemapData : [];
    this.treemapHasData = currentAudiences.length > 0;
    const treemapGrid = document.querySelector('.treemap-grid');

    if (!this.treemapHasData) {
      if (treemapGrid) {
        treemapGrid.classList.remove('audiences-1', 'audiences-2', 'audiences-3', 'audiences-4', 'audiences-5', 'audiences-6');
      }
      for (let i = 0; i < 6; i++) {
        const box = document.querySelector(`.audience-${i + 1}`);
        if (box) {
          (box as HTMLElement).style.display = 'none';
          (box as HTMLElement).style.removeProperty('left');
          (box as HTMLElement).style.removeProperty('top');
          (box as HTMLElement).style.removeProperty('width');
          (box as HTMLElement).style.removeProperty('height');
        }
      }
      return;
    }

    if (treemapGrid) {
      treemapGrid.classList.remove('audiences-1', 'audiences-2', 'audiences-3', 'audiences-4', 'audiences-5', 'audiences-6');
      const allBoxes = Array.from(treemapGrid.querySelectorAll('.audience-box')) as HTMLElement[];
      allBoxes.forEach(box => {
        box.style.removeProperty('left');
        box.style.removeProperty('top');
        box.style.removeProperty('width');
        box.style.removeProperty('height');
      });

      if (currentAudiences.length >= 2 && currentAudiences.length <= 6) {
        treemapGrid.classList.add(`audiences-${currentAudiences.length}`);
      }

      if (currentAudiences.length >= 2) {
        this.applyProportionalSizing(treemapGrid as HTMLElement, currentAudiences);
      } else if (currentAudiences.length === 1) {
        const singleBox = treemapGrid.querySelector('.audience-1') as HTMLElement | null;
        if (singleBox) {
          singleBox.style.position = 'absolute';
          singleBox.style.left = '0px';
          singleBox.style.top = '0px';
          singleBox.style.width = '100%';
          singleBox.style.height = '100%';
        }
      }
    }

    currentAudiences.forEach((audienceData, index) => {
      const valueElement = document.querySelector(`.audience-${index + 1} .audience-value`);
      if (valueElement) {
        const [numberPart, percentagePart = ''] = (audienceData.label || '').split('|').map((part: any) => part.trim());
        const numberNode = (valueElement as HTMLElement).querySelector('.audience-number');
        const percentageNode = (valueElement as HTMLElement).querySelector('.audience-percentage');
        if (numberNode) {
          numberNode.textContent = numberPart || '';
        }
        if (percentageNode) {
          percentageNode.textContent = percentagePart || '';
        }
      }

      const labelElement = document.querySelector(`.audience-${index + 1} .audience-label`);
      if (labelElement) {
        const labelEl = labelElement as HTMLElement;
        labelEl.textContent = audienceData.name;
      }
    });

    for (let i = currentAudiences.length; i < 6; i++) {
      const box = document.querySelector(`.audience-${i + 1}`);
      if (box) {
        (box as HTMLElement).style.display = 'none';
      }
    }

    for (let i = 0; i < currentAudiences.length; i++) {
      const box = document.querySelector(`.audience-${i + 1}`);
      if (box) {
        (box as HTMLElement).style.display = 'flex';
        (box as HTMLElement).style.backgroundColor = currentAudiences[i].color;
      }
    }
  }

  private applyProportionalSizing(treemapGrid: HTMLElement, audiences: any[]) {
    if (!audiences.length) {
      return;
    }

    const MIN_HEIGHT = 60;
    const MAX_ITEMS_PER_ROW = 2;

    const enriched = audiences.map((audience, index) => ({
      originalIndex: index,
      value: Number(audience.value) || 0
    }));

    const sorted = [...enriched].sort((a, b) => b.value - a.value);

    interface TreemapRow {
      items: typeof enriched;
      totalValue: number;
    }

    const rows: TreemapRow[] = [];
    sorted.forEach(item => {
      let currentRow = rows[rows.length - 1];
      if (!currentRow || currentRow.items.length >= MAX_ITEMS_PER_ROW) {
        currentRow = { items: [], totalValue: 0 };
        rows.push(currentRow);
      }
      currentRow.items.push(item);
      currentRow.totalValue += item.value;
    });

    const totalValue = rows.reduce((sum, row) => sum + row.totalValue, 0);
    const gridRect = treemapGrid.getBoundingClientRect();
    let containerWidth = gridRect.width || treemapGrid.clientWidth || treemapGrid.offsetWidth;
    let containerHeight = gridRect.height || treemapGrid.clientHeight || treemapGrid.offsetHeight;

    if (containerWidth <= 0) {
      containerWidth = treemapGrid.parentElement?.clientWidth || 1;
    }
    if (containerHeight <= 0) {
      containerHeight = Math.max(MIN_HEIGHT * rows.length, 320);
    }

    const baseHeights = rows.map(row => {
      if (totalValue <= 0) {
        return containerHeight / rows.length;
      }
      return (row.totalValue / totalValue) * containerHeight;
    });

    const { heights: adjustedHeights, totalHeight } =
      this.enforceRowHeightMinimums(baseHeights, MIN_HEIGHT, containerHeight);

    treemapGrid.style.height = `${totalHeight}px`;
    const treemapContainer = treemapGrid.closest('.audience-treemap-raw') as HTMLElement | null;
    if (treemapContainer) {
      treemapContainer.style.height = `${totalHeight}px`;
    }

    let currentY = 0;
    rows.forEach((row, rowIndex) => {
      const rowHeight = adjustedHeights[rowIndex];
      const rowWidth = containerWidth;
      const rowValue = row.totalValue > 0 ? row.totalValue : row.items.length;

      let currentX = 0;
      row.items.forEach((item, itemIndex) => {
        let boxWidth = rowWidth;
        if (row.items.length > 1) {
          const share = item.value > 0 ? item.value / rowValue : 1 / row.items.length;
          boxWidth = rowWidth * share;
        }
        if (itemIndex === row.items.length - 1) {
          boxWidth = Math.max(rowWidth - currentX, 0);
        }

        const box = treemapGrid.querySelector(`.audience-${item.originalIndex + 1}`) as HTMLElement | null;
        if (box) {
          box.style.position = 'absolute';
          box.style.left = `${currentX}px`;
          box.style.top = `${currentY}px`;
          box.style.width = `${Math.max(boxWidth, 0)}px`;
          box.style.height = `${Math.max(rowHeight, MIN_HEIGHT)}px`;
        }

        currentX += boxWidth;
      });

      currentY += rowHeight;
    });
  }

  private enforceRowHeightMinimums(baseHeights: number[], minHeight: number, targetHeight: number): { heights: number[]; totalHeight: number } {
    if (!baseHeights.length) {
      return { heights: [], totalHeight: 0 };
    }

    const heights = baseHeights.map(height => Math.max(height, minHeight));
    let total = heights.reduce((sum, height) => sum + height, 0);
    let desiredTotal = Math.max(targetHeight, minHeight * baseHeights.length);

    if (total > desiredTotal) {
      let excess = total - desiredTotal;

      while (excess > 0.1) {
        const adjustable = heights
          .map((height, index) => ({ index, slack: height - minHeight }))
          .filter(item => item.slack > 0.1);

        if (!adjustable.length) {
          break;
        }

        const totalSlack = adjustable.reduce((sum, item) => sum + item.slack, 0);
        if (totalSlack <= 0) {
          break;
        }

        adjustable.forEach(item => {
          if (excess <= 0) {
            return;
          }
          const portion = item.slack / totalSlack;
          const reduction = Math.min(item.slack, portion * excess);
          heights[item.index] -= reduction;
          excess -= reduction;
        });

        total = heights.reduce((sum, height) => sum + height, 0);
      }

      if (total > desiredTotal) {
        desiredTotal = total;
      }
    } else if (total < desiredTotal) {
      const deficit = desiredTotal - total;
      const increase = deficit / heights.length;
      for (let i = 0; i < heights.length; i++) {
        heights[i] += increase;
      }
      total = heights.reduce((sum, height) => sum + height, 0);
    }

    return { heights, totalHeight: Math.max(total, desiredTotal) };
  }

  private updatePersonasValues(totalUsersData: any) {
    // Update personas display values with data from API (similar to dashboard)
    const totalUsersValue = document.querySelector('.total-users .value');
    const totalUsersPercentage = document.querySelector('.total-users .percentage') as HTMLElement | null;

    if (totalUsersValue && totalUsersData.value) {
      totalUsersValue.textContent = totalUsersData.value;
    }
    if (totalUsersPercentage) {
      if (totalUsersData.percentage && totalUsersData.percentage.trim() !== '') {
        totalUsersPercentage.textContent = totalUsersData.percentage;
        totalUsersPercentage.style.display = 'inline';

        if (totalUsersData.growthType === 'positive') {
          totalUsersPercentage.style.color = '#10b981';
        } else if (totalUsersData.growthType === 'negative') {
          totalUsersPercentage.style.color = '#ef4444';
        } else {
          totalUsersPercentage.style.color = '#6b7280';
        }
      } else {
        totalUsersPercentage.textContent = '';
        totalUsersPercentage.style.display = 'none';
      }
    }
  }

  /** TOTAL USERS (LINE) — identico al dashboard */
  private createTotalUsersChart(data?: any[]) {
    const ctx = document.getElementById('totalUsersPersonasChart') as HTMLCanvasElement;
    if (!ctx) {
      console.error('Canvas totalUsersPersonasChart not found.');
      return;
    }

    // Use provided data
    const chartData = Array.isArray(data) ? data : [];
    this.totalUsersHasData = chartData.length > 0;

    if (!this.totalUsersHasData) {
      const context = ctx.getContext('2d');
      if (context) {
        context.clearRect(0, 0, ctx.width, ctx.height);
      }
      return;
    }

    const values = chartData.map(d => Number(d.users) || 0);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const padding = (maxValue - minValue) * 0.1 || maxValue * 0.1 || 1;

    this.totalUsersChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: chartData.map(d => d.date),
        datasets: [{
          data: chartData.map(d => d.users),
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderWidth: 4,
          fill: false,
          tension: 0.4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { display: false },
          y: {
            display: false,
            suggestedMin: minValue - padding,
            suggestedMax: maxValue + padding
          }
        }
      }
    });
  }

  /** USERS CATEGORY (BAR ORIZZONTALE) — identico al dashboard (palette e spessori) */
  private createUsersCategoryPersonasChart(data?: any[]) {
    const ctx = document.getElementById('usersCategoryPersonasChart') as HTMLCanvasElement;
    if (!ctx) {
      console.error('Canvas usersCategoryPersonasChart not found.');
      return;
    }

    // Use provided data
    const chartData = Array.isArray(data) ? data : [];
    this.usersCategoryHasData = chartData.length > 0;

    if (!this.usersCategoryHasData) {
      const context = ctx.getContext('2d');
      if (context) {
        context.clearRect(0, 0, ctx.width, ctx.height);
      }
      return;
    }

    try {
      this.usersCategoryChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: chartData.map(d => d.category),
          datasets: [{
            data: chartData.map(d => d.percentage),
            backgroundColor: ['#1e40af', '#1e40af', '#1e40af'], // = dashboard
            barThickness: 35,
            maxBarThickness: 40
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          indexAxis: 'y',
          plugins: { legend: { display: false } },
          scales: {
            x: { display: false, grid: { display: false }, border: { display: false } },
            y: {
              display: true,
              grid: { display: false },
              border: { display: false },
              ticks: { font: { size: 11 }, color: '#333' }
            }
          },
          layout: { padding: { left: 10, right: 10 } }
        },
        plugins: [{
          id: 'categoryPercentagesPersonas',
          afterDatasetsDraw: (chart: any) => {
            const ctx = chart.ctx;
            const dataset = chart.data.datasets[0];
            if (!dataset) {
              return;
            }

            ctx.save();
            ctx.font = 'bold 13px Gantari';
            ctx.fillStyle = '#ffffff';
            ctx.textBaseline = 'middle';

            const metadata = chart.getDatasetMeta(0);
            metadata.data.forEach((element: any, index: number) => {
              const value = dataset.data[index];
              if (value === undefined || value === null) {
                return;
              }
              const text = `${value}%`;
              const baseX = element.base ?? 0;
              const endX = element.x ?? baseX;
              const labelX = baseX + (endX - baseX) / 2;
              const labelY = element.y ?? 0;

              ctx.textAlign = 'center';
              ctx.fillText(text, labelX, labelY);
            });

            ctx.restore();
          }
        }]
      });
    } catch (error) {
      console.error('Error creating category chart:', error);
    }
  }

  /** USERS BY DEVICE (DOUGHNUT) — identico al dashboard (tooltip + percentuali disegnate onComplete) */
  private createUsersDevicePersonasChart(): void {
    const ctx = document.getElementById('usersDevicePersonasChart') as HTMLCanvasElement;
    if (!ctx) {
      console.error('Canvas usersDevicePersonasChart not found.');
      return;
    }

    const slices = this.usersDeviceDataByMetric[this.selectedDeviceMetric] || [];
    this.usersDeviceHasData = slices.length > 0;
    const maxLegendLength = 18;
    this.usersDeviceLegend = slices.map(slice => ({
      label: slice.label.length > maxLegendLength ? `${slice.label.slice(0, maxLegendLength)}…` : slice.label,
      fullLabel: slice.label,
      color: slice.color
    }));

    this.usersDeviceChart?.destroy();

    if (!this.usersDeviceHasData) {
      this.usersDeviceLegend = [];
      const context = ctx.getContext('2d');
      if (context) {
        context.clearRect(0, 0, ctx.width, ctx.height);
      }
      return;
    }

    try {
      const labelThreshold = 8;

      this.usersDeviceChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: slices.map(slice => slice.label),
          datasets: [{
            data: slices.map(slice => slice.raw),
            backgroundColor: slices.map(slice => slice.color),
            borderWidth: 0,
            borderColor: 'transparent'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '60%',
          plugins: {
            legend: { display: false },
            tooltip: {
              enabled: true,
              displayColors: false,
              callbacks: {
                title: () => '',
                label: (context) => {
                  const slice = slices[context.dataIndex];
                  if (!slice) {
                    return '';
                  }
                  const lines = slice.label.split(' - ');
                  lines.push(`${this.formatNumber(slice.raw)} (${slice.value}%)`);
                  return lines;
                }
              }
            }
          },
          layout: {
            padding: {
              top: 12,
              right: 12,
              bottom: 12,
              left: 12
            }
          },
          animation: {
            animateRotate: true
          }
        },
        plugins: [{
          id: 'percentageLabels',
          afterDatasetsDraw: (chart: any) => {
            const ctx = chart.ctx;
            ctx.save();
            ctx.font = 'bold 13px Gantari';
            ctx.fillStyle = 'white';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            chart.data.datasets.forEach((dataset: any, i: number) => {
              const meta = chart.getDatasetMeta(i);
              meta.data.forEach((element: any, index: number) => {
                // Use the pre-calculated percentage from slices instead of raw data
                const slice = slices[index];
                if (slice && slice.value >= labelThreshold) {
                  const { x, y } = element.tooltipPosition(false);
                  ctx.fillText(slice.value + '%', x, y);
                }
              });
            });
            ctx.restore();
          }
        }]
      });
    } catch (error) {
      console.error('Error creating device chart:', error);
    }
  }

  /** USERS BY AUDIENCE (grafico grande in basso): ottimizzato per visibilità */
  private createAudiencePersonasChart(data?: any[]) {
    const ctx = document.getElementById('audiencePersonasChart') as HTMLCanvasElement;
    if (!ctx) {
      console.error('Canvas audiencePersonasChart not found.');
      return;
    }

    // Use provided data
    const audienceData = Array.isArray(data) ? data : [];
    const hasTrendData = audienceData.length > 0 && this.selectedAudiences.length > 0;
    this.audienceTrendHasData = hasTrendData;

    if (!hasTrendData) {
      const context = ctx.getContext('2d');
      if (context) {
        context.clearRect(0, 0, ctx.width, ctx.height);
      }
      return;
    }


    const dataMatrix = audienceData.map(d => d.values);
    const flatValues = dataMatrix.flat().filter(v => typeof v === 'number' && !isNaN(v)) as number[];
    let yMin = flatValues.length ? Math.min(...flatValues) : 0;
    let yMax = flatValues.length ? Math.max(...flatValues) : 1;

    if (yMin === yMax) {
      const padding = yMin === 0 ? 1 : Math.abs(yMin) * 0.1;
      yMin -= padding;
      yMax += padding;
    }

    this.audienceChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: audienceData.map(d => d.month),
        datasets: this.selectedAudiences.map((audience, index) => ({
          label: audience.name,
          data: audienceData.map(d => d.values[index]),
          borderColor: audience.color,
          backgroundColor: audience.color + '15',
          borderWidth: 5, // Increased from 4 to 5
          fill: false,
          tension: 0.3, // Slightly less tension for smoother curves
          pointRadius: 5, // Increased from 4 to 5
          pointHoverRadius: 8, // Increased from 7 to 8
          pointBackgroundColor: audience.color,
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointHoverBorderWidth: 3,
          segment: {
            //rimosso per rimuovere differenze tra le linee
            // borderDash: index === 0 ? [] : index === 1 ? [5, 5] : index === 2 ? [10, 5] : [15, 5, 5, 5] // Different line styles for better distinction
          }
        }))
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            mode: 'index',
            intersect: false,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            titleColor: '#333',
            bodyColor: '#666',
            borderColor: '#ddd',
            borderWidth: 1
          }
        },
        scales: {
          x: {
            display: true,
            grid: {
              display: true,
              color: '#f0f0f0',
              lineWidth: 1
            },
            ticks: {
              font: { size: 11 },
              color: '#666',
              padding: 8
            }
          },
          y: {
            display: true,
            grid: {
              display: true,
              color: '#f0f0f0',
              lineWidth: 1
            },
            ticks: {
              font: { size: 11 },
              color: '#666',
              padding: 8
            },
            suggestedMin: yMin,
            suggestedMax: yMax
          }
        },
        interaction: {
          intersect: false,
          mode: 'index'
        },
        animation: {
          duration: 1000,
          easing: 'easeInOutQuart'
        }
      }
    });
  }

  // Device metric selector methods (same as dashboard)
  onDeviceMetricChange(metric: DeviceMetricId) {
    this.selectedDeviceMetric = metric;

    this.createUsersDevicePersonasChart();
  }

  get selectedDeviceMetricLabel(): string {
    const metric = this.availableDeviceMetrics.find(item => item.id === this.selectedDeviceMetric);
    return metric?.name || 'dashboard.deviceMetrics.device';
  }


  private getDeviceColor(device: string): string {
    switch (device.toLowerCase()) {
      case 'mobile': return '#1e40af';
      case 'desktop': return '#3b82f6';
      case 'tablet': return '#93c5fd';
      case 'smart tv': return '#60a5fa';
      default: return '#6b7280';
    }
  }

  protected readonly Object = Object;

  // Process methods to transform raw API data locally (no additional HTTP calls)
  private processTotalUsersData(
    totalsResponse: any,
    dailyResponse: any,
    selectedRange: DateRangeOption,
    comparisonDailyResponse?: any
  ): any {
    if (totalsResponse.error || dailyResponse.error || comparisonDailyResponse?.error) {
      throw new Error(
        totalsResponse.error?.message ||
        dailyResponse.error?.message ||
        comparisonDailyResponse?.error?.message
      );
    }

    const totalsPayload = Array.isArray(totalsResponse.payload) ? totalsResponse.payload : [];
    const totalsRow = totalsPayload[0] || {};
    const totalUsers = parseInt(totalsRow.TNCIDs || '0', 10) || 0;
    const dailyData = this.sortDailyEntries(Array.isArray(dailyResponse.payload) ? dailyResponse.payload : []);
    const comparisonDataRaw = Array.isArray(comparisonDailyResponse?.payload)
      ? comparisonDailyResponse.payload
      : dailyData;
    const comparisonData = this.sortDailyEntries(comparisonDataRaw);

    const windowSize = this.getWindowSizeForRange(selectedRange);
    let percentage = '';
    let growthType: 'positive' | 'negative' | 'neutral' = 'neutral';

    if (selectedRange !== '30days' && windowSize && comparisonData.length > windowSize) {
      const recentSlice = comparisonData.slice(-windowSize);
      let previousSlice = comparisonData.slice(-(windowSize * 2), -windowSize);

      if (previousSlice.length === 0) {
        previousSlice = comparisonData.slice(0, Math.max(0, comparisonData.length - windowSize));
      }

      const recentTotal = this.sumTncIds(recentSlice);
      const previousTotal = this.sumTncIds(previousSlice);

      const recentAverage = recentSlice.length > 0 ? recentTotal / recentSlice.length : 0;
      const previousAverage = previousSlice.length > 0 ? previousTotal / previousSlice.length : 0;

      if (previousAverage > 0) {
        const growth = ((recentAverage - previousAverage) / previousAverage) * 100;
        if (Math.abs(growth) < 0.0001) {
          percentage = '0.0%';
          growthType = 'neutral';
        } else {
          growthType = growth >= 0 ? 'positive' : 'negative';
          percentage = `${growth >= 0 ? '+' : ''}${growth.toFixed(1)}%`;
        }
      }
    }

    return {
      value: this.formatNumber(totalUsers),
      percentage,
      growthType,
      chartData: dailyData.map((d: any) => ({
        date: this.formatDate(d.Date),
        users: d.TNCIDs
      }))
    };
  }

  private processUsersCategoryData(totalsResponse: any, filters: any[]): any[] {
    if (totalsResponse.error) {
      throw new Error(totalsResponse.error.message);
    }

    const totalsPayload = Array.isArray(totalsResponse.payload) ? totalsResponse.payload : [];
    const data = totalsPayload[0] || {};
    const totalUsers = parseInt(data.TNCIDs || '0') || 0;

    const enrichedColumn = filters.find(f =>
      f.ObjectType.toLowerCase() === 'enriched' &&
      f.ObjectName.toLowerCase() === 'clusters'
    );

    let clusterUsers = 0;
    if (enrichedColumn) {
      clusterUsers = parseInt(data[enrichedColumn.ColumnName] || '0');
    }

    const nullUsers = totalUsers - clusterUsers;
    const clusterPerc = totalUsers > 0 ? Math.round((clusterUsers / totalUsers) * 100) : 0;
    const nullPerc = totalUsers > 0 ? Math.round((nullUsers / totalUsers) * 100) : 0;

    return [
      {
        category: 'YES',
        percentage: clusterPerc,
        value: `${this.formatNumber(clusterUsers)} | ${clusterPerc}%`
      },
      {
        category: 'NO',
        percentage: nullPerc,
        value: `${this.formatNumber(nullUsers)} | ${nullPerc}%`
      }
    ];
  }

  private processUsersDeviceData(totalsResponse: any, filters: any[]): Record<DeviceMetricId, DeviceMetricSlice[]> {
    if (totalsResponse.error) {
      throw new Error(totalsResponse.error.message);
    }

    const totalsPayload = Array.isArray(totalsResponse.payload) ? totalsResponse.payload : [];
    const data = totalsPayload[0] || {};
    const totalUsers = parseInt(data.TNCIDs || '0', 10) || 0;

    const deviceColumns = filters.filter(f => f.ObjectType === 'device');
    const sourceColumns = filters.filter(f => f.ObjectType === 'source');
    const goalColumns = filters.filter(f => f.ObjectType === 'goal');

    const buildSlices = (columns: any[], getColor: (col: any, index: number) => string): DeviceMetricSlice[] => {
      // First pass: get raw values, filter and sort
      const slicesWithRaw = columns
        .map((col, index) => {
          const raw = parseInt(data[col.ColumnName] || '0', 10) || 0;
          return {
            label: col.ObjectName,
            value: 0, // Will be recalculated
            raw,
            color: getColor(col, index),
            tooltip: '' // Will be recalculated
          };
        })
        .filter(slice => slice.raw > 0)
        .sort((a, b) => b.raw - a.raw)
        .slice(0, 6);

      // Calculate total volume from selected slices only
      const totalVolume = slicesWithRaw.reduce((sum, slice) => sum + slice.raw, 0);

      // Second pass: calculate percentages based on selected slices
      return slicesWithRaw.map(slice => {
        const percentage = totalVolume > 0 ? Math.round((slice.raw / totalVolume) * 100) : 0;
        return {
          ...slice,
          value: percentage,
          tooltip: `${slice.label}: ${this.formatNumber(slice.raw)} (${percentage}%)`
        };
      });
    };

    const deviceSlices = buildSlices(deviceColumns, (col) => this.getDeviceColor(col.ObjectName));
    const sourceSlices = buildSlices(sourceColumns, (_col, index) => this.metricPalette[index % this.metricPalette.length]);
    const goalSlices = buildSlices(goalColumns, (_col, index) => this.metricPalette[index % this.metricPalette.length]);

    return {
      device: deviceSlices,
      source: sourceSlices,
      goal: goalSlices
    };
  }

  private processTreemapData(totalsResponse: any, filters: any[], audienceIds: string[], audienceColors: any): any[] {
    if (totalsResponse.error) {
      throw new Error(totalsResponse.error.message);
    }

    const totalsPayload = Array.isArray(totalsResponse.payload) ? totalsResponse.payload : [];
    const data = totalsPayload[0] || {};
    const totalUsers = parseInt(data.TNCIDs || '0') || 0;

    const audienceColumns = filters.filter(f =>
      f.ObjectType === 'personas' && audienceIds.includes(f.ColumnName)
    );

    return audienceColumns.map(audCol => {
      const audUsers = parseInt(data[audCol.ColumnName] || '0') || 0;
      const percentage = totalUsers > 0 ? Math.round((audUsers / totalUsers) * 100) : 0;

      return {
        id: audCol.ColumnName,
        name: audCol.ObjectName,
        value: percentage,
        user_count: audUsers,
        color: audienceColors[audCol.ColumnName] || '#3b82f6',
        label: `${this.formatNumber(audUsers)} | ${percentage}%`
      };
    }).filter(item => item.user_count > 0);
  }

  private processAudiencePublicData(dailyTrendResponse: any, audienceColumns: string[]): any[] {
    if (dailyTrendResponse.error) {
      throw new Error(dailyTrendResponse.error.message);
    }

    const dailyData = Array.isArray(dailyTrendResponse.payload) ? dailyTrendResponse.payload : [];

    return dailyData.map((d: any) => ({
      month: this.formatDate(d.Date),
      values: audienceColumns.map(col => d[col] || 0)
    }));
  }

  private formatNumber(num: number): string {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  }

  private formatDate(dateStr: { value: string } | string): string {
    if (!dateStr) return '';
    const raw = typeof dateStr === 'string' ? dateStr : dateStr?.value;
    const date = new Date(raw);
    const day = date.getDate();
    const monthNames = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
    const month = monthNames[date.getMonth()];
    return `${day} ${month}`;
  }

  exportToExcel() {
    if (!this.isTableView) {
      window.alert('Please switch to table view to export data');
      return;
    }

    const wsData: (string | number)[][] = [];
    const headers: string[] = [];
    if (this.columnVisibility.personas) headers.push('Personas');
    if (this.columnVisibility.source) headers.push('Source');
    if (this.columnVisibility.device) headers.push('Device');
    if (this.columnVisibility.users) headers.push('Users');
    if (this.columnVisibility.goal) headers.push('Goal');

    wsData.push(headers);

    this.expandedRows.forEach(row => {
      const dataRow: (string | number)[] = [];
      if (this.columnVisibility.personas) dataRow.push(row.personas || row.audience || '');
      if (this.columnVisibility.source) dataRow.push(row.source || '');
      if (this.columnVisibility.device) dataRow.push(row.device || '');
      if (this.columnVisibility.users) dataRow.push(row.userCount || 0);
      if (this.columnVisibility.goal) dataRow.push(row.goal || '');
      wsData.push(dataRow);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(wsData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Personas');
    const fileName = `personas-data-${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  }

  exportToPNG() {
    const exportElement = this.isTableView
      ? document.querySelector('.table-container') as HTMLElement
      : document.querySelector('.dashboard-grid') as HTMLElement;

    if (!exportElement) {
      window.alert('Nothing to export');
      return;
    }

    html2canvas(exportElement, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
      allowTaint: true
    }).then(canvas => {
      canvas.toBlob(blob => {
        if (blob) {
          const fileName = `personas-${this.isTableView ? 'table' : 'charts'}-${new Date().toISOString().split('T')[0]}.png`;
          saveAs(blob, fileName);
        }
      });
    }).catch(error => {
      console.error('[PERSONAS] Error exporting PNG:', error);
      window.alert('Error exporting PNG. Please try again.');
    });
  }
}
